/**
 * @fileoverview Shared automation request validators for definitions,
 * triggers, concurrency, catch-up policy, and output routing.
 *
 * @see doc/product/domains/automations/definition-triggers-runs.md - trigger and run contract
 * @see doc/product/domains/automations/output-routing.md - track-issue and chat-output rules
 * @see doc/product/workflows/automation-to-output.md - composed automation workflow
 */
import { z } from "zod";
import {
  AUTOMATION_CATCH_UP_POLICIES,
  AUTOMATION_CONCURRENCY_POLICIES,
  AUTOMATION_OUTPUT_MODES,
  AUTOMATION_STATUSES,
  AUTOMATION_TRIGGER_SIGNING_MODES,
  ISSUE_PRIORITIES,
} from "../constants.js";
import { issueAssigneeAdapterOverridesSchema } from "./issue.js";

const automationTextFieldSchema = z.string().optional().nullable();

const automationBodyFieldsSchema = z.object({
  projectId: z.string().uuid().optional().nullable().default(null),
  goalId: z.string().uuid().optional().nullable(),
  parentIssueId: z.string().uuid().optional().nullable(),
  title: z.string().trim().min(1).max(200),
  instructions: automationTextFieldSchema,
  description: automationTextFieldSchema,
  assigneeAgentId: z.string().uuid(),
  assigneeAgentRuntimeOverrides: issueAssigneeAdapterOverridesSchema.optional().nullable(),
  priority: z.enum(ISSUE_PRIORITIES).optional().default("medium"),
  status: z.enum(AUTOMATION_STATUSES).optional().default("active"),
  concurrencyPolicy: z.enum(AUTOMATION_CONCURRENCY_POLICIES).optional().default("coalesce_if_active"),
  catchUpPolicy: z.enum(AUTOMATION_CATCH_UP_POLICIES).optional().default("skip_missed"),
  outputMode: z.enum(AUTOMATION_OUTPUT_MODES).optional().default("track_issue"),
  chatConversationId: z.string().uuid().optional().nullable().default(null),
  notifyOnIssueCreated: z.boolean().optional().default(false),
});

function normalizeAutomationInstructions<T extends { instructions?: string | null; description?: string | null }>(value: T) {
  const { instructions, ...rest } = value;
  return {
    ...rest,
    description: instructions === undefined ? value.description : instructions,
  };
}

function normalizeAutomationNotifications<T extends { outputMode?: string; notifyOnIssueCreated?: boolean }>(value: T): T {
  if (value.outputMode !== "chat_output" || !value.notifyOnIssueCreated) return value;
  return { ...value, notifyOnIssueCreated: false };
}

export const createAutomationSchema = automationBodyFieldsSchema.superRefine((value, ctx) => {
  if (value.chatConversationId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["chatConversationId"],
      message: "Chat output creates an automation-owned conversation; existing chats cannot be selected",
    });
  }
}).transform(normalizeAutomationInstructions).transform(normalizeAutomationNotifications);

export type CreateAutomation = z.infer<typeof createAutomationSchema>;

export const updateAutomationSchema = automationBodyFieldsSchema.partial().superRefine((value, ctx) => {
  if (value.chatConversationId) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["chatConversationId"],
      message: "Chat output creates an automation-owned conversation; existing chats cannot be selected",
    });
  }
}).transform(normalizeAutomationInstructions).transform(normalizeAutomationNotifications);
export type UpdateAutomation = z.infer<typeof updateAutomationSchema>;

const baseTriggerSchema = z.object({
  label: z.string().trim().max(120).optional().nullable(),
  enabled: z.boolean().optional().default(true),
});

export const createAutomationTriggerSchema = z.discriminatedUnion("kind", [
  baseTriggerSchema.extend({
    kind: z.literal("schedule"),
    cronExpression: z.string().trim().min(1),
    timezone: z.string().trim().min(1).default("UTC"),
  }),
  baseTriggerSchema.extend({
    kind: z.literal("webhook"),
    signingMode: z.enum(AUTOMATION_TRIGGER_SIGNING_MODES).optional().default("bearer"),
    replayWindowSec: z.number().int().min(30).max(86_400).optional().default(300),
  }),
  baseTriggerSchema.extend({
    kind: z.literal("api"),
  }),
]);

export type CreateAutomationTrigger = z.infer<typeof createAutomationTriggerSchema>;

export const updateAutomationTriggerSchema = z.object({
  label: z.string().trim().max(120).optional().nullable(),
  enabled: z.boolean().optional(),
  cronExpression: z.string().trim().min(1).optional().nullable(),
  timezone: z.string().trim().min(1).optional().nullable(),
  signingMode: z.enum(AUTOMATION_TRIGGER_SIGNING_MODES).optional().nullable(),
  replayWindowSec: z.number().int().min(30).max(86_400).optional().nullable(),
});

export type UpdateAutomationTrigger = z.infer<typeof updateAutomationTriggerSchema>;

export const runAutomationSchema = z.object({
  triggerId: z.string().uuid().optional().nullable(),
  payload: z.record(z.unknown()).optional().nullable(),
  idempotencyKey: z.string().trim().max(255).optional().nullable(),
  source: z.enum(["manual", "api"]).optional().default("manual"),
});

export type RunAutomation = z.infer<typeof runAutomationSchema>;

export const rotateAutomationTriggerSecretSchema = z.object({});
export type RotateAutomationTriggerSecret = z.infer<typeof rotateAutomationTriggerSecretSchema>;
